import axios from 'axios';

const url = '/api/v1/tipdirect/questionnaires';

/**
 * Card 323-325 — guest questionnaires.
 *
 * Same-origin session cookie like the rest of the web app. The backend gates
 * every call on the company's questionnaires switch, and the admin calls on a
 * system_admin role, so these carry no checks of their own.
 */

const data = res => res.data.data;

// --- guide (card 323) --------------------------------------------------------

// The tours this guide may open a feedback QR for: their assigned tours that
// have a questionnaire, or every questionnaire tour when none are assigned.
export const fetchFeedbackTours = () => axios.get(`${url}/mine`).then(data);

// Opens (or re-opens) the QR for one departure. Same tour and date returns the
// same token, so tapping through twice does not split a coach's answers.
export const openFeedbackSession = ({ tourId, departureDate }) =>
  axios.post(`${url}/sessions`, { tourId, departureDate }).then(data);

// { session: null } when the guide has no departure still taking answers.
export const fetchCurrentFeedbackSession = () => axios.get(`${url}/sessions/current`).then(data);

// --- admin (cards 324-325) ---------------------------------------------------

export const fetchQuestionnaires = () => axios.get(url).then(data);

export const fetchQuestionnaire = id => axios.get(`${url}/${id}`).then(data);

export const createQuestionnaire = body => axios.post(url, body).then(data);

export const updateQuestionnaire = (id, body) => axios.put(`${url}/${id}`, body).then(data);

export const setQuestionnaireStatus = (id, status) =>
  axios.put(`${url}/${id}/status`, { status }).then(data);

// Every live company tour, with the active questionnaire each is on.
export const fetchQuestionnaireTourOptions = () => axios.get(`${url}/tours`).then(data);

export const fetchQuestionnaireAlerts = () => axios.get(`${url}/alerts`).then(data);

export const saveQuestionnaireAlerts = body => axios.put(`${url}/alerts`, body).then(data);

export const fetchQuestionnaireResponses = params =>
  axios.get(`${url}/responses`, { params }).then(data);

export const fetchQuestionnaireResponse = id => axios.get(`${url}/responses/${id}`).then(data);

// Take a response out and erase what the guest wrote (2026-10-07).
// reason: 'guest_request' | 'not_genuine'.
export const removeQuestionnaireResponse = (id, reason) =>
  axios.post(`${url}/responses/${id}/remove`, { reason }).then(data);

// { privacyUrl } — the privacy policy the guest form links to (2026-10-07).
export const fetchQuestionnairePrivacy = () => axios.get(`${url}/privacy`).then(data);

export const saveQuestionnairePrivacy = privacyUrl =>
  axios.put(`${url}/privacy`, { privacyUrl }).then(data);

export const fetchResponseFilterOptions = params =>
  axios.get(`${url}/responses/filters`, { params }).then(data);

export const responsesCsvUrl = params => {
  const query = new URLSearchParams(
    Object.entries(params || {}).filter(([, value]) => value !== undefined && value !== '')
  ).toString();
  return `${url}/responses.csv${query ? `?${query}` : ''}`;
};

// Card 326 — the same report the digest attaches, for the current filters.
export const reportPdfUrl = params => {
  const query = new URLSearchParams(
    Object.entries(params || {}).filter(([, value]) => value !== undefined && value !== '')
  ).toString();
  return `${url}/report.pdf${query ? `?${query}` : ''}`;
};

// --- insights (card 338) -----------------------------------------------------

// { insight } for one tour (or all) and period. Generated on first view, which
// can take a few seconds; `ifReady: 1` returns only what already exists.
export const fetchQuestionnaireInsights = params =>
  axios.get(`${url}/insights`, { params }).then(data);

// Regenerate now. Refused within an hour of the last one for the same scope.
export const refreshQuestionnaireInsights = body =>
  axios.post(`${url}/insights/refresh`, body).then(data);

// One hotel or experience: every guest's rating of it (2026-10-02).
// params: { kind, name, place, tourId?, from?, to? }
export const fetchQuestionnaireElement = params =>
  axios.get(`${url}/element`, { params }).then(data);

/** The backend's error message, for showing an admin why a save was refused. */
export const apiErrorMessage = error =>
  error?.response?.data?.error?.message || error?.message || 'Something went wrong';
